import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Warehouse, WarehouseType, WarehouseStatus } from '../types/admin';

interface WarehousesResponse {
  success: boolean;
  data: Warehouse[];
}

interface WarehouseResponse {
  success: boolean;
  data: Warehouse;
}

export interface WarehouseInput {
  name: string;
  address: string;
  placeId: string;
  note: string;
  type?: WarehouseType;
  status?: WarehouseStatus;
}

export function useOrgWarehouses(orgId: string) {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<WarehousesResponse>(`/org-admin/${orgId}/warehouses`)
      .then(res => {
        if (!cancelled) {
          setWarehouses(res.data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load warehouses');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [orgId]);

  const createWarehouse = useCallback(
    async (input: WarehouseInput) => {
      const res = await api.post<WarehouseResponse>(`/org-admin/${orgId}/warehouses`, input);
      setWarehouses(prev => [...prev, res.data]);
      return res.data;
    },
    [orgId],
  );

  const updateWarehouse = useCallback(
    async (warehouseId: string, input: Partial<WarehouseInput>) => {
      const res = await api.patch<WarehouseResponse>(`/org-admin/${orgId}/warehouses/${warehouseId}`, input);
      setWarehouses(prev => prev.map(w => w.id === warehouseId ? res.data : w));
      return res.data;
    },
    [orgId],
  );

  const deleteWarehouse = useCallback(
    async (warehouseId: string) => {
      await api.del<{ success: boolean }>(`/org-admin/${orgId}/warehouses/${warehouseId}`);
      setWarehouses(prev => prev.filter(w => w.id !== warehouseId));
    },
    [orgId],
  );

  return { warehouses, loading, error, createWarehouse, updateWarehouse, deleteWarehouse };
}
